import { fareService } from '../services/fareService';

/**
 * SINGLE SOURCE OF TRUTH for pricing calculation
 * Fetches price from Supabase database using zone_prices, transport_coefficients, etc.
 * 
 * @param {Object} params - Pricing parameters
 * @param {string} params.fromZone - Pickup zone code (e.g., 'A', 'B', 'C')
 * @param {string} params.toZone - Destination zone code
 * @param {string} params.transportType - Service key: 'motorbike', 'sedan', 'premium', 'suv4x4', 'boat', 'van'
 * @param {number} params.passengerCount - Number of passengers
 * @param {Date|string} params.pickupDateTime - Pickup date/time (for time coefficient)
 * @param {boolean} params.isFullMoon - Whether it's full moon night (overrides time coefficient)
 * @param {string} params.weatherType - Weather: 'normal', 'rain', 'storm'
 * @returns {Promise<number|null>} Final calculated price in THB, or null if unable to calculate
 */
export async function calculateFare({
  fromZone,
  toZone,
  transportType,
  passengerCount = 1,
  pickupDateTime = new Date(),
  isFullMoon = false,
  weatherType = 'normal'
}) {
  try {
    // Validate required parameters
    if (!fromZone || !toZone || !transportType) {
      console.error('Missing required parameters for fare calculation');
      return null;
    }

    // Step 1: Get base price from zone_prices
    let basePrice = await fareService?.getBasePrice(fromZone, toZone);
    
    // If not found, try reverse lookup
    if (!basePrice) {
      basePrice = await fareService?.getBasePrice(toZone, fromZone);
    }
    
    if (!basePrice) {
      console.error(`No price found for route ${fromZone} → ${toZone}`);
      return null;
    }

    // Step 2: Get transport coefficient
    const transportCoef = await fareService?.getTransportCoefficient(transportType);
    if (!transportCoef) {
      console.error(`No coefficient found for transport type: ${transportType}`);
      return null;
    }

    // Step 3: Calculate time coefficient
    const timeCoef = calculateTimeCoefficient(pickupDateTime, isFullMoon);

    // Step 4: Get weather coefficient (default to 1.0 if not found)
    const weatherCoef = (await fareService?.getWeatherCoefficient(weatherType)) || 1.0;

    // Step 5: Calculate passenger surcharge
    const passengerSurcharge = calculatePassengerSurcharge(transportType, passengerCount);

    // Step 6: Calculate final price
    let price = basePrice * transportCoef * timeCoef * weatherCoef + passengerSurcharge;
    price = Math.round(price);

    return price;

  } catch (error) {
    console.error('Error calculating fare:', error);
    return null;
  }
}

/**
 * Calculate time coefficient based on pickup time
 * @param {Date|string} pickupDateTime - Pickup date/time
 * @param {boolean} isFullMoon - Whether it's full moon night
 * @returns {number} Time coefficient (1.0, 1.2, 1.5, or 1.8 for full moon)
 */
function calculateTimeCoefficient(pickupDateTime, isFullMoon) {
  const date = pickupDateTime instanceof Date ? pickupDateTime : new Date(pickupDateTime);
  const hour = date?.getHours();

  // Full moon override
  if (isFullMoon) {
    return 1.8;
  }

  // Day (06:00-18:00)
  if (hour >= 6 && hour < 18) {
    return 1.0;
  }
  
  // Evening (18:00-23:00)
  if (hour >= 18 && hour < 23) {
    return 1.2;
  }
  
  // Night (23:00-06:00)
  return 1.5;
}

/**
 * Calculate passenger surcharge based on vehicle capacity
 * @param {string} transportType - Service type key
 * @param {number} passengerCount - Number of passengers
 * @returns {number} Additional surcharge amount
 */
function calculatePassengerSurcharge(transportType, passengerCount) {
  // Define max passengers for each service type
  const maxPassengers = {
    motorbike: 2,
    sedan: 4,
    premium: 4,
    suv4x4: 4,
    boat: 10,
    van: 10
  };

  const maxForType = maxPassengers?.[transportType] || 4;

  if (passengerCount <= maxForType) {
    return 0; // No surcharge
  }

  // 100 THB per passenger over capacity
  return 100 * (passengerCount - maxForType);
}

/**
 * Get service types list for UI
 * NORMALIZED keys matching database transport_type column
 * 
 * ⚠️ IMPORTANT: Icons are added at component level, not here
 * This utility file only provides service metadata
 */

export const SERVICE_TYPES = [
  { 
    key: 'motorbike', 
    name: 'Motorbike Taxi', 
    maxPassengers: 2,
    alt: 'Circular yellow icon with navy blue motorbike taxi silhouette',
    gradient: 'from-orange-400 to-orange-600'
  },
  { 
    key: 'sedan', 
    name: 'Taxi', 
    maxPassengers: 4,
    alt: 'Circular yellow icon with navy blue sedan taxi and TAXI sign',
    gradient: 'from-yellow-400 to-yellow-600'
  },
  { 
    key: 'premium', 
    name: 'Premium Taxi', 
    maxPassengers: 4,
    alt: 'Circular yellow icon with navy blue luxury sedan silhouette',
    gradient: 'from-purple-400 to-purple-600'
  },
  { 
    key: 'suv4x4', 
    name: 'SUV 4x4', 
    maxPassengers: 4,
    alt: 'Circular yellow icon with navy blue SUV 4x4 vehicle silhouette',
    gradient: 'from-green-400 to-green-600'
  },
  { 
    key: 'boat', 
    name: 'Boat Taxi', 
    maxPassengers: 10,
    alt: 'Circular yellow icon with navy blue boat taxi silhouette',
    gradient: 'from-blue-400 to-blue-600'
  },
  { 
    key: 'van', 
    name: 'Minivan', 
    maxPassengers: 10,
    alt: 'Circular yellow icon with navy blue minivan silhouette',
    gradient: 'from-indigo-400 to-indigo-600'
  }
];